import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@services/api-client';

/**
 * CentraOne Plus x HuHoKa CRM link (plan 14, Phase 1). The browser only ever
 * talks to the BFF; HuHoKa's credentials never reach it (plan section 3,
 * principle 3).
 */
export type HuhokaLinkStatus = 'NOT_CONNECTED' | 'PENDING' | 'ACTIVE' | 'ERROR' | 'DISCONNECTED';

export interface HuhokaStatus {
  status: HuhokaLinkStatus;
  /** False when this environment has no HuHoKa platform registration. */
  configured: boolean;
  linkId: string | null;
  role: 'initiator' | 'responder' | null;
  remoteTenantName: string | null;
  remoteWebUrl: string | null;
  connectedByName: string | null;
  connectedAt: string | null;
  lastSyncAt: string | null;
  lastError: string | null;
  /** Client sync (Phase 2). */
  syncState: HuhokaSyncState;
  syncMode: 'initial' | 'resync' | null;
  /** Clients handled by the current or last run. */
  syncProcessed: number;
  syncStartedAt: string | null;
  syncFinishedAt: string | null;
  syncError: string | null;
  /** Customers HuHoKa CRM currently manages. */
  syncedCount: number;
  /** Clients that could not be synced (CSV download). */
  skippedCount: number;
}

export type HuhokaSyncState = 'idle' | 'pending' | 'running' | 'completed' | 'failed';

/** The query HuHoKa CRM puts on our approve page (handshake contract section 2). */
export interface HuhokaAuthorizeRequest {
  client_id?: string;
  redirect_uri?: string;
  state?: string;
  code_challenge?: string;
  code_challenge_method?: string;
  link_id?: string;
  remote_tenant_id?: string;
  remote_tenant_name?: string;
}

export interface HuhokaAuthorizeInfo {
  remoteTenantName: string | null;
  localTenantName: string;
  sharedData: string[];
}

/** What HuHoKa CRM decided for a ticket's lead (plan 14 Phase 4). */
export interface HuhokaLeadOutcome {
  status: 'created' | 'existing_lead' | 'existing_client';
  leadUid: string | null;
  clientUid: string | null;
  /** https address of the lead / client in HuHoKa CRM, or null. */
  webUrl: string | null;
}

export interface HuhokaLeadState {
  ticketId: string;
  /** The Create lead button may be offered. */
  linkActive: boolean;
  outcome: HuhokaLeadOutcome | null;
  /** pending: queued or retrying; failed: HuHoKa refused it (lastError says why). */
  delivery: 'none' | 'pending' | 'done' | 'failed';
  lastError: string | null;
}

const BASE = '/integrations/huhoka';

export const huhokaApi = {
  status: async (): Promise<HuhokaStatus> => (await apiClient.get<HuhokaStatus>(`${BASE}/status`)).data,
  connect: async (): Promise<{ linkId: string; authorizeUrl: string }> =>
    (await apiClient.post<{ linkId: string; authorizeUrl: string }>(`${BASE}/connect`)).data,
  complete: async (body: { state: string; code?: string; error?: string }): Promise<HuhokaStatus> =>
    (await apiClient.post<HuhokaStatus>(`${BASE}/complete`, body)).data,
  validate: async (request: HuhokaAuthorizeRequest): Promise<HuhokaAuthorizeInfo> =>
    (await apiClient.post<HuhokaAuthorizeInfo>(`${BASE}/authorize/validate`, request)).data,
  approve: async (request: HuhokaAuthorizeRequest): Promise<{ redirectUrl: string }> =>
    (await apiClient.post<{ redirectUrl: string }>(`${BASE}/authorize/approve`, request)).data,
  deny: async (request: HuhokaAuthorizeRequest): Promise<{ redirectUrl: string }> =>
    (await apiClient.post<{ redirectUrl: string }>(`${BASE}/authorize/deny`, request)).data,
  disconnect: async (): Promise<HuhokaStatus> => (await apiClient.post<HuhokaStatus>(`${BASE}/disconnect`)).data,
  resync: async (): Promise<HuhokaStatus> => (await apiClient.post<HuhokaStatus>(`${BASE}/resync`)).data,
  skippedCsv: async (): Promise<Blob> =>
    (await apiClient.get<Blob>(`${BASE}/skipped.csv`, { responseType: 'blob' })).data,
  leadState: async (ticketId: string): Promise<HuhokaLeadState> =>
    (await apiClient.get<HuhokaLeadState>(`${BASE}/tickets/${encodeURIComponent(ticketId)}/lead`)).data,
  createLead: async (ticketId: string): Promise<HuhokaLeadState> =>
    (await apiClient.post<HuhokaLeadState>(`${BASE}/tickets/${encodeURIComponent(ticketId)}/lead`)).data,
};

/** Saves the skipped-clients CSV through a temporary object URL. */
export async function downloadSkippedClients(): Promise<void> {
  const blob = await huhokaApi.skippedCsv();
  const url = URL.createObjectURL(blob);
  try {
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = 'huhoka-skipped-clients.csv';
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
  } finally {
    URL.revokeObjectURL(url);
  }
}

export const huhokaKeys = {
  status: (tenantId?: string) => ['integrations', 'huhoka', 'status', tenantId] as const,
  lead: (ticketId?: string) => ['integrations', 'huhoka', 'lead', ticketId] as const,
};

/** A ticket's lead state; polls while the lead is still being created. */
export function useHuhokaLead(ticketId?: string) {
  return useQuery({
    queryKey: huhokaKeys.lead(ticketId),
    queryFn: () => huhokaApi.leadState(ticketId as string),
    enabled: !!ticketId,
    staleTime: 30_000,
    retry: false,
    refetchInterval: (query) => (query.state.data?.delivery === 'pending' ? 5_000 : false),
  });
}

export function useCreateHuhokaLead(ticketId?: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => huhokaApi.createLead(ticketId as string),
    onSuccess: (state) => queryClient.setQueryData(huhokaKeys.lead(ticketId), state),
  });
}

export function useHuhokaStatus(tenantId?: string) {
  return useQuery({
    queryKey: huhokaKeys.status(tenantId),
    queryFn: huhokaApi.status,
    enabled: !!tenantId,
    staleTime: 15_000,
    // While a connection waits for approval in HuHoKa, or clients are syncing,
    // keep the card current.
    refetchInterval: (query) => {
      const data = query.state.data;
      return data?.status === 'PENDING' || isSyncInProgress(data) ? 5_000 : false;
    },
  });
}

export function isSyncInProgress(status: HuhokaStatus | undefined): boolean {
  return status?.status === 'ACTIVE' && (status.syncState === 'pending' || status.syncState === 'running');
}

export function useHuhokaResync(tenantId?: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: huhokaApi.resync,
    onSuccess: (status) => queryClient.setQueryData(huhokaKeys.status(tenantId), status),
  });
}

export function useHuhokaDisconnect(tenantId?: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: huhokaApi.disconnect,
    onSuccess: (status) => queryClient.setQueryData(huhokaKeys.status(tenantId), status),
  });
}

/** The server's message for a failed request, for a toast or an inline error. */
export function errorMessage(error: unknown, fallback: string): string {
  const data = (error as { response?: { data?: { message?: unknown; error?: { message?: unknown } } } })?.response
    ?.data;
  const message = data?.message ?? data?.error?.message;
  if (Array.isArray(message)) return message.join(', ');
  return typeof message === 'string' && message ? message : fallback;
}

/** HTTP status of a failed request, if any. */
export function errorStatus(error: unknown): number | undefined {
  return (error as { response?: { status?: number } })?.response?.status;
}
